import { FC, useEffect } from 'react'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'

type ModalType = 'completed' | 'processing' | 'error'

export interface ServiceProgressModalProps {
  visible: boolean
  type: ModalType
  statusText?: string
  onClose: () => void
}

const ICON_MAP: Record<ModalType, string> = {
  completed: '/images/service-progress/icon-success.svg',
  processing: '/images/service-progress/icon-processing.svg',
  error: '/images/service-progress/icon-error.svg',
}

// 状态标签的背景色和文字色
const BADGE_STYLE: Record<'completed' | 'processing', { bg: string; text: string }> = {
  completed: { bg: 'bg-[#E8F8EF]', text: 'text-[#00B365]' },
  processing: { bg: 'bg-[#DBEAFF]', text: 'text-pdblue' },
}

const ServiceProgressModal: FC<ServiceProgressModalProps> = ({
  visible,
  type,
  statusText,
  onClose,
}) => {
  const t = useTranslations()

  // 弹窗打开时禁止背景滚动
  useEffect(() => {
    if (!visible) return
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
  }, [visible])

  if (!visible) return null

  const isError = type === 'error'
  const title = isError
    ? t('ServiceProgressModal.QueryFailed')
    : t('ServiceProgressModal.QuerySuccess')

  const description = isError
    ? t('ServiceProgressModal.ErrorDesc')
    : type === 'completed'
      ? t('ServiceProgressModal.CompletedDesc')
      : t('ServiceProgressModal.ProcessingDesc')

  const badge = !isError && statusText
    ? { label: t('ServiceProgressModal.StatusLabel', { status: statusText }), style: BADGE_STYLE[type as 'completed' | 'processing'] }
    : null

  return (
    <div
      className={clsx(
        'fixed inset-0 z-[51]',
        'bg-black/50',
        'flex items-center justify-center',
        'px-6 laptop:px-0'
      )}
      onClick={onClose}
    >
      <div
        className={clsx(
          'w-full laptop:w-[480px]',
          'bg-white',
          'rounded-lg laptop:rounded-lg',
          'p-5 laptop:p-12',
          'flex flex-col items-center',
          'gap-6'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 图标 */}
        <img
          src={ICON_MAP[type]}
          alt=""
          className={clsx('w-12 h-12')}
        />

        {/* 内容区域 */}
        <div className={clsx('w-full', 'flex flex-col items-center', 'gap-3')}>
          {/* 标题 */}
          <h3
            className={clsx(
              'text-24',
              'font-semibold',
              'text-black/90'
            )}
          >
            {title}
          </h3>

          {/* 状态标签 */}
          {badge && (
            <span
              className={clsx(
                badge.style.bg,
                badge.style.text,
                'text-14 font-semibold',
                'px-3 py-1',
                'rounded'
              )}
            >
              {badge.label}
            </span>
          )}

          {/* 描述 */}
          <p
            className={clsx(
              'text-14 laptop:text-14',
              'text-black/60',
              'text-center'
            )}
          >
            {description}
          </p>
        </div>

        {/* 确定按钮 */}
        <button
          type="button"
          onClick={onClose}
          className={clsx(
            'w-full laptop:w-40',
            'h-10',
            'bg-pdblue',
            'rounded-[2px]',
            'text-14 laptop:text-16',
            'font-semibold',
            'text-white',
            'hover:opacity-90',
            'transition-opacity'
          )}
        >
          {t('ServiceProgressModal.OK')}
        </button>
      </div>
    </div>
  )
}

export default ServiceProgressModal
